import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type { FindOrganizationUsersQueryResponse } from "../types/FindOrganizationUsers"

type FindOrganizationUsersClient = typeof client<
  FindOrganizationUsersQueryResponse,
  Error,
  never
>
type FindOrganizationUsers = {
  data: FindOrganizationUsersQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<FindOrganizationUsersClient>>
  client: {
    parameters: Partial<Parameters<FindOrganizationUsersClient>[0]>
    return: Awaited<ReturnType<FindOrganizationUsersClient>>
  }
}
export const findOrganizationUsersQueryKey = () =>
  [{ url: "/v1/client-user/find-organization-users" }] as const
export type FindOrganizationUsersQueryKey = ReturnType<
  typeof findOrganizationUsersQueryKey
>
export function findOrganizationUsersQueryOptions<
  TData = FindOrganizationUsers["response"],
  TQueryData = FindOrganizationUsers["response"],
>(
  options: FindOrganizationUsers["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindOrganizationUsers["response"],
    FindOrganizationUsers["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findOrganizationUsersQueryKey()
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindOrganizationUsers["data"],
        FindOrganizationUsers["error"]
      >({
        method: "get",
        url: `/v1/client-user/find-organization-users`,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find organization users
 * @link /v1/client-user/find-organization-users
 */
export function useFindOrganizationUsers<
  TData = FindOrganizationUsers["response"],
  TQueryData = FindOrganizationUsers["response"],
  TQueryKey extends QueryKey = FindOrganizationUsersQueryKey,
>(
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindOrganizationUsers["response"],
        FindOrganizationUsers["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindOrganizationUsers["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindOrganizationUsers["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? findOrganizationUsersQueryKey()
  const query = useQuery<
    FindOrganizationUsers["data"],
    FindOrganizationUsers["error"],
    TData,
    any
  >({
    ...findOrganizationUsersQueryOptions<TData, TQueryData>(clientOptions),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindOrganizationUsers["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
